import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";
import { Helper } from "src/app/core/helpers/helper";

declare const google: any;

@Injectable({
  providedIn: "root"
})
export class MapHelperService {
  public themeModeSubscription!: Subscription;
  private mapboxModule: Promise<any> | null = null;

  constructor(private helper: Helper) {}

  stylesForTheme(theme: string | null): any[] {
    if (theme === "rhero-light") return [];
    if (theme === "rhero-black") return this.helper.darkModeStyles || [];
    if (theme === "forest") return this.forestMapStyles;
    if (theme === "sand") return this.sandMapStyles;
    if (theme === "slate-blue") return this.slateMapStyles;
    if (!theme) return [];
    const water: Record<string, string> = {
      ocean: "#0c4a6e",
      graphite: "#1e293b",
      dusk: "#312e81",
      "midnight-teal": "#115e59",
      "rhero-dark": "#134e4a"
    };
    const base = this.helper.darkModeStyles || [];
    return water[theme] ? [...base, { featureType: "water", elementType: "geometry", stylers: [{ color: water[theme] }] }] : base;
  }

  readonly blackMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#0a0a0a" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#e5e5e5" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#0a0a0a" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#1c1c1c" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1a24" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] }
  ];

  readonly forestMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#f2f6f1" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#b7d4c8" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c6e6cf" }] }
  ];

  readonly sandMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#f6f1e7" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#d7c4a3" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#fffbf5" }] }
  ];

  readonly slateMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#e7edf4" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#bfdbfe" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] }
  ];

  readonly lightFleetStyles = [
    { elementType: "geometry", stylers: [{ color: "#f4f5f7" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#3e4651" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#f4f5f7" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", stylers: [{ visibility: "on" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#d7eadb" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dbe3ee" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9ddf5" }] }
  ];

  /** Cloud map ID for this panel, served in the login settings. Empty = none configured. */
  private get cloudMapId(): string {
    return this.helper.dispatcherDetails?.setting?.mapId || "";
  }

  initializeGoogleMap(elementId: string, mapOptions: any): any {
    const mapElement: HTMLElement | null = document.getElementById(elementId);
    if (!mapElement || typeof google === "undefined" || !google?.maps?.Map) return null;

    const mapId = this.cloudMapId;

    // Two styling mechanisms, and they cancel each other out: passing a `styles` array to a map
    // DISABLES whatever cloud style its map ID points at, and Google logs a warning. So the panel
    // picks one per map and never mixes them.
    //
    // With a map ID the look lives in Google Cloud Console ("R-Hero Navigation"), including its own
    // dark variant, so the per-theme arrays below are not applied at all. Without one — the default
    // until an ID is configured in settings — nothing changes and the local styles keep working.
    if (mapId) {
      // ColorScheme is what picks between the light and dark styles attached to the map ID, so it
      // has to be seeded from the panel's current theme or a dark desk renders a light map. The
      // dispatcher's extra themes (forest / sand / slate-blue / ocean / …) have no cloud equivalent,
      // so anything that is not the plain light theme maps to DARK. Read through a guard because the
      // enum is absent on older Maps JS versions, and the loader is still pinned to v3.64.
      const theme = document.documentElement.getAttribute("data-rh-theme");
      const colorScheme = google.maps.ColorScheme
        ? theme && theme !== "rhero-light"
          ? google.maps.ColorScheme.DARK
          : google.maps.ColorScheme.LIGHT
        : undefined;

      // No theme subscription on purpose. A cloud map's light/dark comes from `colorScheme`, which
      // Google only reads when the map is CONSTRUCTED; calling setOptions afterwards does nothing.
      // Re-themeing therefore means rebuilding the map, which is the caller's decision (it owns the
      // markers and listeners), not something to do silently underneath them.
      return new google.maps.Map(mapElement, { ...mapOptions, mapId, ...(colorScheme ? { colorScheme } : {}) });
    }

    const map = new google.maps.Map(mapElement, mapOptions);
    const paint = () => {
      const type = map.getMapTypeId?.();
      if (type === "hybrid" || type === "satellite") return;
      map.setOptions({ styles: this.stylesForTheme(document.documentElement.getAttribute("data-rh-theme")) });
    };
    paint();
    this.themeModeSubscription = this.helper.themeModeChange$.subscribe(() => paint());
    document.documentElement.addEventListener("rhero-theme", paint);
    return map;
  }

  /**
   * Mapbox stays out of the initial bundle. The chunk loads the first time a
   * Mapbox map is actually created (Vehicle Track, or a Mapbox-configured desk).
   */
  loadMapbox(): Promise<any> {
    if (!this.mapboxModule) {
      this.mapboxModule = import("mapbox-gl").then((mod: any) => (mod?.Map ? mod : mod?.default || mod));
    }
    return this.mapboxModule;
  }

  async initializeMapboxMap(elementId: string, mapOptions: any): Promise<any> {
    const mapboxgl = await this.loadMapbox();
    mapboxgl.accessToken = this.helper.MapBoxAccessToken;

    const mapElement: HTMLElement | null = document.getElementById(elementId);
    if (!mapElement) return null;

    return new mapboxgl.Map({
      container: mapElement,
      ...mapOptions
    });
  }
}
